/**
 * Example File Search Service
 *
 * Provides searching, filtering, and sorting capabilities for example files.
 */

import type {
  ExampleFileMetadata,
  ExampleSearchQuery,
  ExampleSearchResult,
  ExampleFilterFacets,
  ExampleSearchResultWithFacets,
  ExampleCategory,
  BuildingType,
  ComplexityLevel,
  HVACSystemType,
  PlantEquipmentType,
  LearningConcept,
} from '../types';
import { EXAMPLE_FILES } from '../data/exampleIndex';

/**
 * Simple fuzzy text matching score.
 */
function fuzzyMatch(text: string, query: string): number {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Exact match
  if (lowerText === lowerQuery) return 1;

  // Contains match
  if (lowerText.includes(lowerQuery)) {
    return 0.8 + (0.2 * lowerQuery.length) / lowerText.length;
  }

  // Word-based matching
  const queryWords = lowerQuery.split(/\s+/);
  const textWords = lowerText.split(/\s+/);
  let matchedWords = 0;

  for (const qWord of queryWords) {
    for (const tWord of textWords) {
      if (tWord.includes(qWord) || qWord.includes(tWord)) {
        matchedWords++;
        break;
      }
    }
  }

  if (matchedWords > 0) {
    return 0.5 * (matchedWords / queryWords.length);
  }

  return 0;
}

/**
 * Search examples with text matching across multiple fields.
 */
function textSearch(
  query: string,
  examples: ExampleFileMetadata[]
): ExampleSearchResult[] {
  const results: ExampleSearchResult[] = [];

  for (const example of examples) {
    // Search across multiple fields
    const nameScore = fuzzyMatch(example.name, query);
    const descScore = fuzzyMatch(example.description, query) * 0.8;
    const filenameScore = fuzzyMatch(example.filename, query) * 0.9;

    // Search tags
    let tagScore = 0;
    for (const tag of example.tags) {
      const score = fuzzyMatch(tag, query);
      if (score > tagScore) tagScore = score;
    }
    tagScore *= 0.7;

    // Search objects showcased
    let objectScore = 0;
    for (const obj of example.objectsShowcased.primary) {
      const score = fuzzyMatch(obj, query);
      if (score > objectScore) objectScore = score;
    }
    objectScore *= 0.75;

    // Search learning summary
    const learningScore = fuzzyMatch(example.learning.summary, query) * 0.6;

    // Take the highest score
    const maxScore = Math.max(nameScore, descScore, filenameScore, tagScore, objectScore, learningScore);

    if (maxScore > 0.3) {
      const matchedFields: string[] = [];
      if (nameScore > 0.3) matchedFields.push('name');
      if (descScore > 0.3) matchedFields.push('description');
      if (filenameScore > 0.3) matchedFields.push('filename');
      if (tagScore > 0.3) matchedFields.push('tags');
      if (objectScore > 0.3) matchedFields.push('objects');
      if (learningScore > 0.3) matchedFields.push('learning');

      results.push({
        ...example,
        score: maxScore,
        matchedFields,
      });
    }
  }

  return results;
}

/**
 * Filter by category.
 */
function filterByCategory(
  examples: ExampleFileMetadata[],
  categories: ExampleCategory | ExampleCategory[]
): ExampleFileMetadata[] {
  const categoryArray = Array.isArray(categories) ? categories : [categories];
  return examples.filter((e) => categoryArray.includes(e.category));
}

/**
 * Filter by building type.
 */
function filterByBuildingType(
  examples: ExampleFileMetadata[],
  buildingTypes: BuildingType | BuildingType[]
): ExampleFileMetadata[] {
  const typeArray = Array.isArray(buildingTypes) ? buildingTypes : [buildingTypes];
  return examples.filter((e) => typeArray.includes(e.buildingType));
}

/**
 * Filter by complexity level.
 */
function filterByComplexity(
  examples: ExampleFileMetadata[],
  levels: ComplexityLevel | ComplexityLevel[]
): ExampleFileMetadata[] {
  const levelArray = Array.isArray(levels) ? levels : [levels];
  return examples.filter((e) => levelArray.includes(e.complexity));
}

/**
 * Filter by HVAC system type.
 */
function filterByHVACSystem(
  examples: ExampleFileMetadata[],
  systems: HVACSystemType | HVACSystemType[]
): ExampleFileMetadata[] {
  const systemArray = Array.isArray(systems) ? systems : [systems];
  return examples.filter((e) => {
    if (!e.hvacSystems) return false;
    return e.hvacSystems.some((s) => systemArray.includes(s));
  });
}

/**
 * Filter by plant equipment type.
 */
function filterByPlantEquipment(
  examples: ExampleFileMetadata[],
  equipment: PlantEquipmentType | PlantEquipmentType[]
): ExampleFileMetadata[] {
  const equipmentArray = Array.isArray(equipment) ? equipment : [equipment];
  return examples.filter((e) => {
    if (!e.plantEquipment) return false;
    return e.plantEquipment.some((eq) => equipmentArray.includes(eq));
  });
}

/**
 * Filter by learning concept.
 */
function filterByConcept(
  examples: ExampleFileMetadata[],
  concepts: LearningConcept | LearningConcept[]
): ExampleFileMetadata[] {
  const conceptArray = Array.isArray(concepts) ? concepts : [concepts];
  return examples.filter((e) => {
    return e.learning.concepts.some((c) => conceptArray.includes(c));
  });
}

/**
 * Filter by EnergyPlus object class.
 */
function filterByObjectClass(
  examples: ExampleFileMetadata[],
  objectClasses: string | string[]
): ExampleFileMetadata[] {
  const classArray = Array.isArray(objectClasses) ? objectClasses : [objectClasses];
  const lowerClassArray = classArray.map((c) => c.toLowerCase());

  return examples.filter((e) => {
    const allObjects = [
      ...e.objectsShowcased.primary,
      ...(e.objectsShowcased.secondary ?? []),
    ];
    return allObjects.some((obj) => {
      const lowerObj = obj.toLowerCase();
      return lowerClassArray.some((c) => lowerObj.includes(c) || c.includes(lowerObj));
    });
  });
}

/**
 * Filter by tag.
 */
function filterByTag(
  examples: ExampleFileMetadata[],
  tags: string | string[]
): ExampleFileMetadata[] {
  const tagArray = Array.isArray(tags) ? tags : [tags];
  const lowerTagArray = tagArray.map((t) => t.toLowerCase());

  return examples.filter((e) => {
    return e.tags.some((tag) => lowerTagArray.includes(tag.toLowerCase()));
  });
}

/**
 * Sort results by specified criteria.
 */
function sortResults(
  examples: ExampleSearchResult[],
  sortBy: 'name' | 'popularity' | 'complexity' | 'zones',
  sortOrder: 'asc' | 'desc'
): ExampleSearchResult[] {
  const complexityOrder: Record<ComplexityLevel, number> = {
    beginner: 0,
    intermediate: 1,
    advanced: 2,
    expert: 3,
  };

  const sorted = [...examples].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'popularity':
        comparison = (b.popularity ?? 0) - (a.popularity ?? 0);
        break;
      case 'complexity':
        comparison = complexityOrder[a.complexity] - complexityOrder[b.complexity];
        break;
      case 'zones':
        comparison = (a.stats?.zones ?? 0) - (b.stats?.zones ?? 0);
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return sorted;
}

/**
 * Calculate additive facet counts based on current filters.
 */
function calculateFacetCounts(
  baseExamples: ExampleFileMetadata[],
  query: ExampleSearchQuery
): ExampleFilterFacets {
  // For each filter category, calculate counts with OTHER filters applied

  // For categories: apply all other filters except category
  let examplesForCategories = baseExamples;
  if (query.buildingType) examplesForCategories = filterByBuildingType(examplesForCategories, query.buildingType);
  if (query.complexity) examplesForCategories = filterByComplexity(examplesForCategories, query.complexity);
  if (query.hvacSystem) examplesForCategories = filterByHVACSystem(examplesForCategories, query.hvacSystem);
  if (query.plantEquipment) examplesForCategories = filterByPlantEquipment(examplesForCategories, query.plantEquipment);
  if (query.concept) examplesForCategories = filterByConcept(examplesForCategories, query.concept);
  if (query.tag) examplesForCategories = filterByTag(examplesForCategories, query.tag);

  // For building types: apply all other filters except buildingType
  let examplesForBuildingTypes = baseExamples;
  if (query.category) examplesForBuildingTypes = filterByCategory(examplesForBuildingTypes, query.category);
  if (query.complexity) examplesForBuildingTypes = filterByComplexity(examplesForBuildingTypes, query.complexity);
  if (query.hvacSystem) examplesForBuildingTypes = filterByHVACSystem(examplesForBuildingTypes, query.hvacSystem);
  if (query.plantEquipment) examplesForBuildingTypes = filterByPlantEquipment(examplesForBuildingTypes, query.plantEquipment);
  if (query.concept) examplesForBuildingTypes = filterByConcept(examplesForBuildingTypes, query.concept);
  if (query.tag) examplesForBuildingTypes = filterByTag(examplesForBuildingTypes, query.tag);

  // For complexity: apply all other filters except complexity
  let examplesForComplexity = baseExamples;
  if (query.category) examplesForComplexity = filterByCategory(examplesForComplexity, query.category);
  if (query.buildingType) examplesForComplexity = filterByBuildingType(examplesForComplexity, query.buildingType);
  if (query.hvacSystem) examplesForComplexity = filterByHVACSystem(examplesForComplexity, query.hvacSystem);
  if (query.plantEquipment) examplesForComplexity = filterByPlantEquipment(examplesForComplexity, query.plantEquipment);
  if (query.concept) examplesForComplexity = filterByConcept(examplesForComplexity, query.concept);
  if (query.tag) examplesForComplexity = filterByTag(examplesForComplexity, query.tag);

  // For HVAC systems: apply all other filters except hvacSystem
  let examplesForHVAC = baseExamples;
  if (query.category) examplesForHVAC = filterByCategory(examplesForHVAC, query.category);
  if (query.buildingType) examplesForHVAC = filterByBuildingType(examplesForHVAC, query.buildingType);
  if (query.complexity) examplesForHVAC = filterByComplexity(examplesForHVAC, query.complexity);
  if (query.plantEquipment) examplesForHVAC = filterByPlantEquipment(examplesForHVAC, query.plantEquipment);
  if (query.concept) examplesForHVAC = filterByConcept(examplesForHVAC, query.concept);
  if (query.tag) examplesForHVAC = filterByTag(examplesForHVAC, query.tag);

  // For plant equipment: apply all other filters except plantEquipment
  let examplesForPlant = baseExamples;
  if (query.category) examplesForPlant = filterByCategory(examplesForPlant, query.category);
  if (query.buildingType) examplesForPlant = filterByBuildingType(examplesForPlant, query.buildingType);
  if (query.complexity) examplesForPlant = filterByComplexity(examplesForPlant, query.complexity);
  if (query.hvacSystem) examplesForPlant = filterByHVACSystem(examplesForPlant, query.hvacSystem);
  if (query.concept) examplesForPlant = filterByConcept(examplesForPlant, query.concept);
  if (query.tag) examplesForPlant = filterByTag(examplesForPlant, query.tag);

  // For concepts: apply all other filters except concept
  let examplesForConcepts = baseExamples;
  if (query.category) examplesForConcepts = filterByCategory(examplesForConcepts, query.category);
  if (query.buildingType) examplesForConcepts = filterByBuildingType(examplesForConcepts, query.buildingType);
  if (query.complexity) examplesForConcepts = filterByComplexity(examplesForConcepts, query.complexity);
  if (query.hvacSystem) examplesForConcepts = filterByHVACSystem(examplesForConcepts, query.hvacSystem);
  if (query.plantEquipment) examplesForConcepts = filterByPlantEquipment(examplesForConcepts, query.plantEquipment);
  if (query.tag) examplesForConcepts = filterByTag(examplesForConcepts, query.tag);

  // Calculate counts
  const categoryMap = new Map<ExampleCategory, number>();
  for (const e of examplesForCategories) {
    categoryMap.set(e.category, (categoryMap.get(e.category) ?? 0) + 1);
  }

  const buildingTypeMap = new Map<BuildingType, number>();
  for (const e of examplesForBuildingTypes) {
    buildingTypeMap.set(e.buildingType, (buildingTypeMap.get(e.buildingType) ?? 0) + 1);
  }

  const complexityMap = new Map<ComplexityLevel, number>();
  for (const e of examplesForComplexity) {
    complexityMap.set(e.complexity, (complexityMap.get(e.complexity) ?? 0) + 1);
  }

  const hvacMap = new Map<HVACSystemType, number>();
  for (const e of examplesForHVAC) {
    if (e.hvacSystems) {
      for (const s of e.hvacSystems) {
        hvacMap.set(s, (hvacMap.get(s) ?? 0) + 1);
      }
    }
  }

  const plantMap = new Map<PlantEquipmentType, number>();
  for (const e of examplesForPlant) {
    if (e.plantEquipment) {
      for (const eq of e.plantEquipment) {
        plantMap.set(eq, (plantMap.get(eq) ?? 0) + 1);
      }
    }
  }

  const conceptMap = new Map<LearningConcept, number>();
  for (const e of examplesForConcepts) {
    for (const c of e.learning.concepts) {
      conceptMap.set(c, (conceptMap.get(c) ?? 0) + 1);
    }
  }

  // Tags use all filtered results for simplicity
  const tagMap = new Map<string, number>();
  let fullyFiltered = baseExamples;
  if (query.category) fullyFiltered = filterByCategory(fullyFiltered, query.category);
  if (query.buildingType) fullyFiltered = filterByBuildingType(fullyFiltered, query.buildingType);
  if (query.complexity) fullyFiltered = filterByComplexity(fullyFiltered, query.complexity);
  if (query.hvacSystem) fullyFiltered = filterByHVACSystem(fullyFiltered, query.hvacSystem);
  if (query.plantEquipment) fullyFiltered = filterByPlantEquipment(fullyFiltered, query.plantEquipment);
  if (query.concept) fullyFiltered = filterByConcept(fullyFiltered, query.concept);

  for (const e of fullyFiltered) {
    for (const t of e.tags) {
      tagMap.set(t, (tagMap.get(t) ?? 0) + 1);
    }
  }

  return {
    categories: Array.from(categoryMap.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count),
    buildingTypes: Array.from(buildingTypeMap.entries())
      .map(([buildingType, count]) => ({ buildingType, count }))
      .sort((a, b) => b.count - a.count),
    complexityLevels: Array.from(complexityMap.entries())
      .map(([complexity, count]) => ({ complexity, count }))
      .sort((a, b) => {
        const order: ComplexityLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];
        return order.indexOf(a.complexity) - order.indexOf(b.complexity);
      }),
    hvacSystems: Array.from(hvacMap.entries())
      .map(([hvacSystem, count]) => ({ hvacSystem, count }))
      .sort((a, b) => b.count - a.count),
    plantEquipment: Array.from(plantMap.entries())
      .map(([equipment, count]) => ({ equipment, count }))
      .sort((a, b) => b.count - a.count),
    concepts: Array.from(conceptMap.entries())
      .map(([concept, count]) => ({ concept, count }))
      .sort((a, b) => b.count - a.count),
    tags: Array.from(tagMap.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count),
  };
}

/**
 * Search example files with faceted filtering.
 */
export function searchExamplesWithFacets(
  query: ExampleSearchQuery
): ExampleSearchResultWithFacets {
  let baseFiltered: ExampleFileMetadata[] = [...EXAMPLE_FILES];

  // Apply text search first (this is the base for facet calculation)
  if (query.text && query.text.trim().length > 0) {
    const textResults = textSearch(query.text, baseFiltered);
    baseFiltered = textResults;
  }

  // Calculate facets based on current query
  const facets = calculateFacetCounts(baseFiltered, query);

  // Apply all filters for results
  let results: ExampleSearchResult[] = baseFiltered.map((e) => ({ ...e }));

  if (query.category) {
    results = filterByCategory(results, query.category) as ExampleSearchResult[];
  }

  if (query.buildingType) {
    results = filterByBuildingType(results, query.buildingType) as ExampleSearchResult[];
  }

  if (query.complexity) {
    results = filterByComplexity(results, query.complexity) as ExampleSearchResult[];
  }

  if (query.hvacSystem) {
    results = filterByHVACSystem(results, query.hvacSystem) as ExampleSearchResult[];
  }

  if (query.plantEquipment) {
    results = filterByPlantEquipment(results, query.plantEquipment) as ExampleSearchResult[];
  }

  if (query.concept) {
    results = filterByConcept(results, query.concept) as ExampleSearchResult[];
  }

  if (query.objectClass) {
    results = filterByObjectClass(results, query.objectClass) as ExampleSearchResult[];
  }

  if (query.tag) {
    results = filterByTag(results, query.tag) as ExampleSearchResult[];
  }

  // Sort results
  const sortBy = query.sortBy ?? 'popularity';
  const sortOrder = query.sortOrder ?? (sortBy === 'popularity' ? 'desc' : 'asc');
  results = sortResults(results, sortBy, sortOrder);

  // Get total count before pagination
  const totalCount = results.length;

  // Pagination
  const offset = query.offset ?? 0;
  const limit = query.limit ?? 50;
  const paginatedResults = results.slice(offset, offset + limit);
  const hasMore = offset + paginatedResults.length < totalCount;

  return {
    results: paginatedResults,
    totalCount,
    hasMore,
    facets,
  };
}

/**
 * Get example file by ID.
 */
export function getExampleById(id: string): ExampleFileMetadata | undefined {
  return EXAMPLE_FILES.find((e) => e.id === id);
}

/**
 * Get examples by category.
 */
export function getExamplesByCategory(category: ExampleCategory): ExampleFileMetadata[] {
  return EXAMPLE_FILES.filter((e) => e.category === category);
}

/**
 * Get examples by complexity.
 */
export function getExamplesByComplexity(complexity: ComplexityLevel): ExampleFileMetadata[] {
  return EXAMPLE_FILES.filter((e) => e.complexity === complexity);
}

/**
 * Get related examples based on shared concepts and tags.
 */
export function getRelatedExamples(
  exampleId: string,
  limit: number = 5
): ExampleFileMetadata[] {
  const example = getExampleById(exampleId);
  if (!example) return [];

  // Score each example based on shared concepts and tags
  const scores: { example: ExampleFileMetadata; score: number }[] = [];

  for (const e of EXAMPLE_FILES) {
    if (e.id === exampleId) continue;

    let score = 0;

    // Shared concepts (weighted higher)
    for (const concept of e.learning.concepts) {
      if (example.learning.concepts.includes(concept)) {
        score += 2;
      }
    }

    // Shared tags
    for (const tag of e.tags) {
      if (example.tags.includes(tag)) {
        score += 1;
      }
    }

    // Same category
    if (e.category === example.category) {
      score += 1;
    }

    // Shared HVAC systems
    if (example.hvacSystems && e.hvacSystems) {
      for (const sys of e.hvacSystems) {
        if (example.hvacSystems.includes(sys)) {
          score += 1.5;
        }
      }
    }

    // Explicit related examples
    if (example.learning.relatedExamples?.includes(e.id)) {
      score += 5;
    }

    if (score > 0) {
      scores.push({ example: e, score });
    }
  }

  return scores
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.example);
}

/**
 * Get learning path - examples ordered by complexity for a concept.
 */
export function getLearningPath(concept: LearningConcept): ExampleFileMetadata[] {
  const complexityOrder: ComplexityLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];

  return EXAMPLE_FILES
    .filter((e) => e.learning.concepts.includes(concept))
    .sort((a, b) => {
      return complexityOrder.indexOf(a.complexity) - complexityOrder.indexOf(b.complexity);
    });
}

/**
 * Get popular examples.
 */
export function getPopularExamples(limit: number = 10): ExampleFileMetadata[] {
  return [...EXAMPLE_FILES]
    .sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))
    .slice(0, limit);
}

/**
 * Get beginner-friendly examples.
 */
export function getBeginnerExamples(limit: number = 10): ExampleFileMetadata[] {
  return EXAMPLE_FILES
    .filter((e) => e.complexity === 'beginner')
    .sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))
    .slice(0, limit);
}

/**
 * Re-export initial facets for components.
 */
export { getInitialFacets, getExampleCount } from '../data/exampleIndex';
